import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function StoreModal({ open, onClose }) {
  const [products, setProducts] = useState(null);
  const [busy, setBusy] = useState(null); // product_key currently being processed

  useEffect(() => {
    if (open && !products) {
      api.get("/store/products").then(setProducts).catch(() => {});
    }
  }, [open, products]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const checkout = async (product_key) => {
    setBusy(product_key);
    try {
      const res = await api.post("/store/checkout", {
        product_key,
        origin_url: window.location.origin,
      });
      if (res?.url) {
        window.location.href = res.url; // hand off to Stripe
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="store-modal"
      onClick={onClose}
    >
      <div className="panel p-8 max-w-3xl w-full relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 ash-text hover:text-white" data-testid="store-close-btn">
          <X size={18} />
        </button>
        <p className="text-xs tracking-[0.4em] gold-text">THE MERCHANT</p>
        <h3 className="font-heading text-4xl mt-2 text-[#f0e6d2]">Bargain with the <span className="italic gold-text">Ember</span></h3>
        <p className="ash-text mt-2 text-sm italic">Secure checkout · Powered by Stripe.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {products &&
            Object.entries(products.packs).map(([key, p]) => {
              const product_key = `pack_${key}`;
              return (
                <div key={key} className="panel p-5 border-[#d4af37]/40" data-testid={`pack-${key}`}>
                  <div className="text-[10px] tracking-[0.3em] ash-text">EXTRA LIVES</div>
                  <div className="font-heading text-4xl gold-text mt-1">+{p.lives}</div>
                  <div className="font-narrative mt-2 text-lg">${(p.price / 100).toFixed(2)}</div>
                  <button
                    disabled={busy !== null}
                    onClick={() => checkout(product_key)}
                    className="btn-glow w-full mt-5 flex items-center justify-center gap-2"
                    data-testid={`buy-${key}-btn`}
                  >
                    {busy === product_key ? <><Loader2 size={14} className="animate-spin" /> REDIRECTING</> : "PURCHASE"}
                  </button>
                </div>
              );
            })}
        </div>

        {products?.season_pass && (
          <div className="mt-6 panel p-6" data-testid="season-pass-card" style={{ borderColor: "rgba(212,175,55,0.6)", background: "linear-gradient(180deg, rgba(50,35,10,0.5), rgba(15,10,5,0.9))" }}>
            <div className="flex items-start justify-between gap-6 flex-col md:flex-row">
              <div>
                <div className="text-[10px] tracking-[0.3em] gold-text">SEASON PASS · BEST FOR REGULARS</div>
                <div className="font-heading text-3xl text-[#f0e6d2] mt-1">
                  The <span className="italic gold-text">Ember-touched</span> Pact
                </div>
                <ul className="mt-3 text-sm ash-text space-y-1">
                  {products.season_pass.perks.map((perk) => (
                    <li key={perk}>◆ {perk}</li>
                  ))}
                </ul>
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl gold-text">
                  ${(products.season_pass.price_monthly / 100).toFixed(2)}<span className="text-sm ash-text">/mo</span>
                </div>
                <button
                  onClick={() => checkout("season_pass")}
                  disabled={busy !== null}
                  className="btn-primary mt-3 flex items-center gap-2"
                  data-testid="season-pass-btn"
                  style={{ borderColor: "#d4af37", background: "linear-gradient(180deg, #3a2410, #1a0f05)" }}
                >
                  {busy === "season_pass" ? <><Loader2 size={14} className="animate-spin" /> REDIRECTING</> : "CLAIM PASS"}
                </button>
              </div>
            </div>
          </div>
        )}

        {products && (
          <div className="mt-6 panel p-6 border-[#e25822]/50" data-testid="subscription-card">
            <div className="flex items-start justify-between gap-6 flex-col md:flex-row">
              <div>
                <div className="text-[10px] tracking-[0.3em] ember">SUBSCRIPTION</div>
                <div className="font-heading text-3xl text-[#f0e6d2] mt-1">
                  Unlimited <span className="italic gold-text">Audio/Visual</span> Play
                </div>
                <ul className="mt-3 text-sm ash-text space-y-1">
                  {products.subscription.perks.map((perk) => (
                    <li key={perk}>◆ {perk}</li>
                  ))}
                </ul>
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl gold-text">
                  ${(products.subscription.price_monthly / 100).toFixed(2)}<span className="text-sm ash-text">/mo</span>
                </div>
                <button
                  onClick={() => checkout("subscription")}
                  disabled={busy !== null}
                  className="btn-primary mt-3 flex items-center gap-2"
                  data-testid="subscribe-btn"
                >
                  {busy === "subscription" ? <><Loader2 size={14} className="animate-spin" /> REDIRECTING</> : "SUBSCRIBE"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
